import React, { useMemo, memo } from "react";
import type { BiddingInfoResult, BiddingInfoData } from "../../../types";
import { useTruncatedTooltip } from "../../common/TruncatedTooltip";
import {
  CaltivationCategoryOptions,
  ProductionHistoryOptions,
} from "../../../constants";
import dayjs from "dayjs";
import { useAuth } from "../../../contexts/AuthContext";
import { UserRole } from "../../../types/UserRole";
import { Heart } from "lucide-react";
import Big from "big.js";
import { useToggleFavoriteMutation } from "../../../hooks/useBiddingItemsQuery";
import { UserPermission } from "../../../types/UserPermission";

// DBの文字列を受け取り、日本時間のミリ秒付き文字列に変換する
const formatJSTWithMillis = (dateStr: string | undefined | null) => {
  if (!dateStr) return "-";
  // .format() を通すことで、余計なミリ秒をカットし、区切り文字を整えます。
  return dayjs(dateStr).format("YYYY/MM/DD HH:mm:ss.SSS");
};

//----------------
// Props
//----------------
interface BiddingResultItemRowProps {
  item: BiddingInfoResult;
  isExpanded: boolean;
  onToggle: () => void;
  isFavorite: boolean;
  isSellerLayout?: boolean;
}

//--------------------------------
// 落札結果の行コンポーネント
//--------------------------------
export const BiddingResultItemRow = memo(
  ({ item, isExpanded, onToggle, isFavorite, isSellerLayout }: BiddingResultItemRowProps) => {
    const { tooltipProps, TooltipElement } = useTruncatedTooltip(
      "font-medium truncate pointer-events-auto",
    );

    const { currentUser } = useAuth(); // 認証Context
    const loginId = currentUser?.loginId || "";
    const isBuyer = currentUser?.role === UserRole.Buyer;
    
    // 更新用Mutation
    const { mutate, isPending } = useToggleFavoriteMutation(loginId);

    //----------------
    // Memo
    //----------------
    // 総量計算
    const totalWeight = useMemo(() => {
      try {
        // 1. 各値を Big オブジェクトに変換
        const c1 = new Big(item.capacity1 || 0);
        const q1 = new Big(item.quantity1 || 0);
        const c2 = new Big(item.capacity2 || 0);
        const q2 = new Big(item.quantity2 || 0);

        // 2. 10進数で正確に計算
        // w1 = c1 * q1
        const w1 = c1.times(q1);
        // w2 = c2 * q2
        const w2 = c2.times(q2);

        // 3. 結果を合算して number 型で返す
        return w1.plus(w2).toNumber();
      } catch (e) {
        // 数値として読み込めない場合などのセーフティ
        return 0;
      }
    }, [item.capacity1, item.quantity1, item.capacity2, item.quantity2]);

    // // 最高落札価格の表示
    // const displayHighestPrice = useMemo(() => {
    //   if (!item.highestBidPrice || item.highestBidPrice === 0)
    //     return (
    //       <span className="text-slate-400">
    //         <br />
    //       </span>
    //     );
    //   return (
    //     <span className="text-red-600 font-bold">
    //       {Number(item.highestBidPrice).toLocaleString()}
    //     </span>
    //   );
    // }, [item.highestBidPrice]);
    // 最高落札価格の表示
    const displayHighestPrice = useMemo(() => {
      if (!item.highestBidPrice || item.highestBidPrice === 0) {
        return (
          <span className="text-slate-400">
            <br />
          </span>
        );
      }

      // 買手以外 かつ 「落札(抽)」の場合を判定
      const isNotBuyer = currentUser?.role !== UserRole.Buyer;
      const isLotteryWin = item.resultMsg === "落札(抽)";
      const showLottery = isNotBuyer && isLotteryWin;

      if (isNotBuyer){
        return (
          <span className="text-red-600 font-bold">
            {Number(item.highestBidPrice).toLocaleString()}
            {/* 条件を満たさない場合は invisible で透明にし、幅だけを確保する */}
              <span className={showLottery ? "" : "invisible select-none"}>
                (抽)
              </span>
          </span>
        );
      }else{
        return (
          <span className="text-red-600 font-bold">
            {Number(item.highestBidPrice).toLocaleString()}
          </span>
        );
      }
    }, [item.highestBidPrice, item.resultMsg, currentUser?.role]);

    // 落札結果の表示
    // --- 修正後：役割によって「何を見て判定するか」を分ける ---
    const displatBidResult = useMemo(() => {

      const role = currentUser?.role;

      // 買手
      if (role === UserRole.Buyer) {
        // return item.resultMsg ? (
        //   <span className="text-base lg:text-sm text-red-600 font-bold">{item.resultMsg}</span>
        // ) : (
        //   <span className="text-slate-400"><br /></span>
        // );

        if (item.resultMsg) {
          if (item.resultMsg == "- (抽)") {
            // a）他者が落札＆抽選の場合：
            return (
            <span className="text-[14px] text-slate-400 font-medium bg-slate-100 px-2 py-0.5 rounded-md">
                他社落札(抽)
              </span>
            );
          }else{
            // b）自身が落札の場合：
            return (
              <span className="text-base lg:text-sm text-red-600 font-bold">
                {item.resultMsg}
              </span>
            );
          }
        }

        // c）他者が落札の場合：
        if (Number(item.myBidPrice) > 0 && Number(item.highestBidPrice) > 0) {
          return (
            <span className="text-[14px] text-slate-400 font-medium bg-slate-100 px-2 py-0.5 rounded-md">
              他社落札
            </span>
          );
        }

        // d）上記以外の場合：
        return (
          <span className="text-slate-400">
            <br />
          </span>
        );
      }
    
      // 市場
      if (role === UserRole.Admin) {
        if (currentUser?.permission == UserPermission.Admin)
        {
          const hasWinner = item.biddingInfoDatas?.some(bid => bid.isWinningBid);
          return hasWinner ? (
            <span className="text-red-600 font-bold">{item.resultMsg}</span>
          ) : (
            <span className="text-slate-400"><br /></span>
          );
        }
        else
        {
          return item.resultMsg ? (
            <span className="text-base lg:text-sm text-red-600 font-bold">{item.resultMsg}</span>
          ) : (
            <span className="text-slate-400"><br /></span>
          );
        }
      }
    
      // 落札者がいない場合
      return (
        <span className="text-slate-400"><br /></span>
      );
    }, [item.resultMsg, item.biddingInfoDatas, currentUser?.role]);

    // デバッグ用ハンドラ
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation(); // 念の為バブリング防止
      console.log("Row clicked:", item.itemId); // ★コンソールにIDが出るか確認
      onToggle();
    };

    // 落札者名の表示(売手明細用)
    const displayHighestBuyerName = useMemo(() => {
    // highestBidBuyerName 直接使う
    const name = item.highestBidBuyerName;

    // 名前が空（まだ落札されていない、またはデータがない）場合の処理
    if (!name) {
    return (
      <span className="text-slate-400">
        <br />
        </span>
      );
    }
  
    // 名前があれば太字で表示
    return <span className="font-bold">{name}</span>;
    }, [item.highestBidBuyerName]); 

    const handleToggleFavorite = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isPending || !loginId) return;
      mutate({ itemId: item.itemId, branchNumber: item.branchNumber });
    };

    //生産者表示有無フラグ判定ロジック
    const canSeeFarmerName = useMemo(() => {
      // 1. 管理者は常に表示
      if (currentUser?.role === UserRole.Admin) return true;

      // 2. 自分の出品物なら常に表示
      // String() で囲むことで、数値と文字列の比較ミスを防ぎます
      const isOwnItem =
        item.sellerCode &&
        currentUser?.parentCode &&
        String(item.sellerCode) === String(currentUser.parentCode);
      if (isOwnItem) return true;

      // 3. それ以外（買手、または他人の出品物を見る売手）
      // 「隠す設定(Hidden)」が true なら、表示判定は false にする
      return !item.isFarmerHidden;
    }, [
      currentUser?.role,
      currentUser?.parentCode,
      item.sellerCode,
      item.isFarmerHidden, // バックエンドから届くようになったフラグ
    ]);

    //----------------
    // Render
    //----------------
    return (
      <div
        className={`mt-2 lg:mt-4 border-b lg:pb-0 border-slate-800 lg:border lg:rounded-lg lg:shadow-sm overflow-hidden bg-white ${isExpanded ? "ring-2 ring-indigo-500 rounded-lg" : ""}`}
      >
        {/* === ヘッダー行 (常に表示) === */}
        <div className="p-2 lg:p-4 cursor-pointer" onClick={handleClick}>
          <div
            className={`grid grid-cols-6 md:grid-cols-32 gap-2 items-center text-sm pointer-events-none`}
          >
            {isBuyer && (
              <div className="col-span-1 order-1 lg:order-none lg:col-span-1 min-w-[3rem] flex flex-col items-center">
                <button
                  onClick={handleToggleFavorite}
                  disabled={isPending}
                  className="pointer-events-auto p-1 -ml-8 rounded-full hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-50"
                  type="button"
                >
                  <Heart className={`... ${isFavorite ? "fill-red-400 text-red-400" : "text-slate-400"}`} />
                </button>
              </div>
            )}

            {/* 1. 入札番号 */}
            <div className="col-span-1 lg:col-span-2 order-1 lg:order-none">
              <p className="hidden md:block text-xs text-slate-500 mb-0.5">
                入札No.
              </p>
              <p className="text-base lg:font-bold text-slate-800">
                {item.biddingNumber || "-"}
              </p>
            </div>
            {/* 2. 売り手 */}
            <div className="col-span-4 lg:col-span-4 order-2 lg:order-none">
              <p className="hidden md:block text-xs text-slate-500 mb-0.5">
                売手
              </p>
              <p
                {...tooltipProps}
                className="text-lg lg:font-bold pointer-events-auto truncate"
              >
                {item.sellerName || "-"}
              </p>
            </div>
            {/* 3. 生産者 */}
            <div className="col-span-4 lg:col-span-4 order-3 lg:order-none">
              <p className="hidden lg:block text-xs text-slate-500 mb-0.5">
                生産者
              </p>
              <p
                {...tooltipProps}
                className="text-lg lg:font-bold pointer-events-auto truncate"
              >
                {/* 権限がある場合は名前、ない場合は「-」を表示 */}
                {canSeeFarmerName ? (
                  item.farmerName
                ) : (
                  <span className="text-slate-400 font-normal">-</span>
                )}
              </p>
            </div>
            {/* 7. 荷印 */}
            <div className="col-span-2 lg:col-span-3 order-4 lg:order-none">
              <p className="hidden lg:block text-xs text-slate-500 mb-0.5">
                荷印
              </p>
              <p
                {...tooltipProps}
                className="text-lg lg:font-bold pointer-events-auto truncate"
              >
                {item.brand || "-"}
              </p>
            </div>
            {/* PCでは非表示・茶期・茶種・品種 */}
            {/* 4. 茶期 */}
            <div className="col-span-2 hidden md:block">
              <p className="text-xs text-slate-500 mb-0.5">茶期</p>
              <p {...tooltipProps} className="text-lg font-bold truncate">
                {item.teaSeasonName || "-"}
              </p>
            </div>
            {/* 5. 茶種 */}
            <div className="col-span-2 hidden md:block">
              <p className="text-xs text-slate-500 mb-0.5">茶種</p>
              <p {...tooltipProps} className="text-lg font-bold truncate">
                {item.teaTypeName || "-"}
              </p>
            </div>
            {/* 6. 品種 */}
            <div className="col-span-3 hidden md:block">
              <p className="text-xs text-slate-500 mb-0.5">品種名</p>
              <p {...tooltipProps} className="text-lg font-bold pointer-events-auto truncate">
                {item.varietyName || "-"}
              </p>
            </div>
            {/* 8. 総量 */}
            <div className="hidden lg:block col-span-2 text-right">
              <p className="hidden md:block text-xs text-slate-500 mb-0.5">
                総量(kg)
              </p>
              <p className="text-lg lg:font-bold">
                {totalWeight.toLocaleString()}
              </p>
            </div>
            {/* 9. 自身の入札価格 */}
            {currentUser?.role === UserRole.Buyer && (
              <div className="hidden lg:block col-span-3 text-right border-l border-slate-100 pl-2">
                <p className="text-xs text-slate-500 mb-0.5">入札額(円)</p>
                <p className="text-lg font-bold text-blue-700">
                  {Number(item.myBidPrice).toLocaleString()}
                </p>
              </div>
            )}
            
            {/* X. 売手用落札者名表示 */}
            {/* {isSellerLayout && (
              <div className="hidden lg:block col-span-2 text-left border-l border-slate-100 pl-2 min-w-0">
                <p className="text-xs text-slate-500 mb-0.5">落札者名</p>
                <p {...tooltipProps} className="text-lg pointer-events-auto truncate">
                  {displayHighestBuyerName}
                </p>
              </div>
            )} */}
            {!isBuyer && (
              <div className="hidden lg:block col-span-5 text-left border-l border-slate-100 pl-2 min-w-0">
                <p className="text-xs text-slate-500 mb-0.5">落札者名</p>
                <p {...tooltipProps} className="text-lg text-blue-400 pointer-events-auto truncate">
                  {displayHighestBuyerName}
                </p>
              </div>
            )}
            
            {/* 10. 最高落札価格 */}
            <div className="hidden lg:block col-span-3 text-right border-l border-slate-100 pl-2">
              <p className="text-xs text-slate-500 mb-0.5">
                最高落札値(円)
              </p>
              <p className="text-lg">{displayHighestPrice}</p>
            </div>
            {/* 11. 落札結果 */}
            {currentUser?.role === UserRole.Buyer && (
              <div className="hidden lg:block col-span-3 text-center border-l border-slate-100 pl-2">
                <p className="text-xs text-slate-500 mb-0.5">落札結果</p>
                <p className="text-lg">{displatBidResult}</p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:hidden w-full mt-1">
          {/* flex + divide-x で、要素の数に合わせて自動等分 & 境界線 */}
          <div className="flex items-center w-full bg-slate-100 rounded-lg py-1 -translate-y-3 divide-x divide-slate-300">
            {/* 1. 自身の入札価格 or 落札者名 */}
            {currentUser?.role === UserRole.Buyer ? (
              <div className="flex-1 px-1 text-center min-w-0">
                <span className="text-[10px] font-bold text-slate-500 leading-none mb-0.5">自身の入札</span>
                <p className="text-lg font-bold text-blue-700 truncate">
                  {Number(item.myBidPrice).toLocaleString()}
                </p>
              </div>
            // ) : isSellerLayout ? (
            //   // スマホ側の落札者名にもツールチップと省略表示を適用
            //   <div className="flex-1 px-1 text-center min-w-0">
            //     <span className="text-[10px] font-bold text-slate-500 leading-none mb-0.5">落札者</span>
            //     <p {...tooltipProps} className="text-lg pointer-events-auto truncate">
            //       {displayHighestBuyerName}
            //     </p>
            //   </div>
            // ) : null}
            ) : 
              // スマホ側の落札者名にもツールチップと省略表示を適用
              <div className="flex-1 px-1 text-center min-w-0">
                <span className="text-[10px] font-bold text-slate-500 leading-none mb-0.5">落札者</span>
                <p {...tooltipProps} className="text-lg pointer-events-auto truncate">
                  {displayHighestBuyerName}
                </p>
              </div>
            }

            {/* 2. 最高落札価格 (共通) */}
            <div className="flex-1 px-1 text-center">
              <span className="text-[10px] font-bold text-slate-500 leading-none mb-0.5">最高落札値</span>
              <p className="text-lg font-semibold">{displayHighestPrice}</p>
            </div>

            {/* 3. 落札結果 (Buyerのみ表示) */}
            {currentUser?.role === UserRole.Buyer && (
              <div className="flex-1 px-1 text-center min-w-0">
                <span className="text-[10px] font-bold text-slate-500 leading-none mb-0.5">結果</span>
                <p className="text-lg font-bold truncate">{displatBidResult}</p>
              </div>
            )}
          </div>
        </div>

        {/* モバイル用: 隠れている項目の簡易表示 */}
        <div className="md:hidden text-base flex flex-wrap gap-x-3 pointer-events-none justify-left ml-2.5 -translate-y-1.5">
          <span>{item.teaSeasonName}</span>
          <span>/</span>
          <span>{item.teaTypeName}</span>
          <span>/</span>
          <span>{item.varietyName}</span>
          <span>/</span>
          <span>{totalWeight.toLocaleString()}kg</span>
        </div>

        {/* === アコーディオン詳細 === */}
        {isExpanded && (
        <div className="p-1 lg:p-4 bg-slate-50 border-t border-slate-200 animate-fadeIn">
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-y-4 gap-x-2 lg:gap-6">
          {/* 1. GAP区分 */}
          <div>
            <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase mb-1">GAP区分</p>
            <p className="text-base text-slate-700 lg:font-semibold truncate">{item.gapName}</p>
          </div>
            
          {/* 2. 栽培区分 */}
          <div>
            <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase mb-1">栽培区分</p>
            {(() => {
              const targetOption = CaltivationCategoryOptions.find(opt => opt.value === item.caltivationCategory);
              return <p className="text-base text-slate-700 lg:font-semibold">{targetOption ? targetOption.label : "-"}</p>;
            })()}
          </div>
          
          {/* 3. 生産履歴 */}
          <div>
            <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase mb-1">生産履歴</p>
            {(() => {
              const targetOption = ProductionHistoryOptions.find(opt => opt.value === item.productionHistory);
              return <p className="text-base text-slate-700 lg:font-semibold">{targetOption ? targetOption.label : "-"}</p>;
            })()}
          </div>
          
          {/* 4. 容量① */}
          <div>
            <p className="hidden lg:block text-xs font-bold text-slate-500 mb-1">容量(kg)/本数(本) ①</p>
            <p className="text-base text-slate-700 lg:font-semibold truncate">
              {item.capacity1}×{item.quantity1}
            </p>
          </div>
          
          {/* 5. 容量② */}
          <div>
            <p className="hidden lg:block text-xs font-bold text-slate-500 mb-1">容量(kg)/本数(本) ②</p>
            <p className="text-base text-slate-700 lg:font-semibold truncate">
              {item.capacity2 ? `${item.capacity2}×${item.quantity2}` : "-"}
            </p>
          </div>
          
          {/* 6. 最低落札値 */}
          <div>
            <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase mb-1 text-right">最低落札値(円)</p>
            <p className="text-base text-slate-700 lg:font-semibold text-right truncate">
              {Number(item.bidMinPrice).toLocaleString()}
            </p>
          </div>
        </div>
          
        {/* 2段目：備考 (Buyer以外の場合のみ表示) */}
        {currentUser?.role !== UserRole.Buyer && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase">備考</p>
            <p {...tooltipProps} className="text-base text-slate-700 lg:font-semibold leading-relaxed">
              {item.biko || "-"}
            </p>
          </div>
        )}

            {/* 入札履歴テーブル (Adminかつ売手レイアウト「以外」の場合のみ表示) */}
            {/* 市場の編集者/閲覧者(isSellerLayoutがtrue)の場合は入札履歴を表示させない */}
            {currentUser?.role === UserRole.Admin && !isSellerLayout && (
              <div className="border-t border-slate-200 mt-1 ">
                <h4 className="text-sm font-bold text-slate-700 mb-3 mt-3 flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  入札履歴
                </h4>
                {!item.biddingInfoDatas ||
                item.biddingInfoDatas.length === 0 ? (
                  <p className="text-sm text-slate-400 italic p-2">
                    入札履歴はありません。
                  </p>
                ) : (
                  <div className="overflow-x-auto border rounded-md bg-white">
                    <table className="w-full text-sm text-left text-slate-600">
                      <thead className="text-xs text-slate-500 uppercase bg-slate-100 border-b">
                        <tr>
                          <th className="px-4 py-2">入札日時</th>
                          <th className="px-4 py-2">買手名称</th>
                          <th className="px-4 py-2 text-right">入札本数①</th>
                          <th className="px-4 py-2 text-right">入札本数②</th>
                          <th className="px-4 py-2 text-right">入札総量</th>
                          <th className="px-4 py-2 text-right">入札額</th>
                          <th className="px-4 py-2 text-center">結果</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.biddingInfoDatas.map((bid, index) => (
                          <HistoryRow
                            key={`${bid.buyerCode}-${index}`}
                            bid={bid}
                            parentItem={item}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {TooltipElement}
      </div>
    );
  },
);

const HistoryRow = ({
  bid,
  parentItem,
}: {
  bid: BiddingInfoData;
  parentItem: BiddingInfoResult;
}) => {
  const bidTotalWeight = useMemo(() => {
    const w1 = (bid.quantity1 || 0) * (Number(parentItem.capacity1) || 0);
    const w2 = (bid.quantity2 || 0) * (Number(parentItem.capacity2) || 0);
    return w1 + w2;
  }, [bid, parentItem]);

  //バックエンドから最高落札者判定を取得するように変更
  const isWinner = bid.isWinningBid;

  const formattedDate = formatJSTWithMillis(bid.bidTimestamp);

  return (
    <tr
      className={`border-b last:border-0 hover:bg-slate-50 ${isWinner ? "bg-yellow-50" : ""}`}
    >
      <td className="px-4 py-2 whitespace-nowrap text-xs text-slate-500">
        {formattedDate}
      </td>
      <td className="px-4 py-2 font-medium">{bid.buyerName}</td>
      <td className="px-4 py-2 text-right">{bid.quantity1}</td>
      <td className="px-4 py-2 text-right">{bid.quantity2}</td>
      <td className="px-4 py-2 text-right font-medium">{bidTotalWeight} kg</td>
      <td className="px-4 py-2 text-right font-bold text-slate-800">
        {bid.bidPrice.toLocaleString()} 円
      </td>
      <td className="px-4 py-2 text-center">
        {isWinner && (
          <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-600 border border-red-200">
            {parentItem.resultMsg}
          </span>
        )}
        {bid.isDisabled && (
          <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-gray-100 text-gray-600 border border-gray-200">
            無効
          </span>
        )}
      </td>
    </tr>
  );
};